import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { Dropdown } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import moment from 'moment';

import { PermissionEnum } from '../../../types/PermissionEnum';
import { useAuthContext } from '../../Auth/AuthProvider';
import JobMenuItems from '../../Header/JobMenuItemsHeader/JobMenuItems';
import { Loading, DateTimePicker } from '../../_common';
import { toastOpt } from '../../../utils/helpers/toastHelper';

import {
    GET_JOB_PLANNED_STAGES,
    GET_JOB_PLANNED_TRUCKS,
    GET_JOB_SUMMARY_INFO,
} from '../../../graphql/client/queries';
import { SAVE_JOB_PLANNED_STAGES, SAVE_JOB_PLANNED_TRUCKS } from '../../../graphql/client/mutations';
import {
    GetJobPlannedStagesResponse,
    GetJobPlannedTrucksResponse,
    JobMetricsMutationResponse,
} from '../../../types/jobMetricsTypes';
import { GetJobSummaryInfo } from '../../../types/schemaTypes';

import JobMetricEntryModal, { JobMetricValueSubmit } from './JobMetricEntryModal';
import JobMetricMatrixTable, { JobMetricHistoryEntry } from './JobMetricMatrixTable';
import '../DailySandAllocations/DailySandAllocations.scss';
import './JobMetrics.scss';

const DAY_COUNT = 14;

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const dateToDisplay = (d: Date): string => {
    const m = d.getMonth() + 1;
    const day = d.getDate();
    return `${m}-${day}-${d.getFullYear()} 00:00:00`;
};

const dateToUtcMidnightIso = (d: Date): string =>
    new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();

const JobMetrics = (): React.ReactElement => {
    const { jobId } = useParams();
    const jobIdVal: number = jobId ? parseInt(jobId, 10) : 0;

    const authContext = useAuthContext();
    // Reuse Set-Tonnage permission until a dedicated JobMetrics permission is provisioned.
    const canEditJobMetrics = authContext.userHasPermission(PermissionEnum.SandAllocationSetTonnage);

    const today = new Date();
    const fourteenDaysFromToday = new Date();
    fourteenDaysFromToday.setDate(today.getDate() + DAY_COUNT - 1);

    const [fromDate, setFromDate] = useState<string>(dateToDisplay(today));
    const [toDate, setToDate] = useState<string>(dateToDisplay(fourteenDaysFromToday));
    const [datePickerFromDate, setDatePickerFromDate] = useState<any>();

    const [trucksFromDate, setTrucksFromDate] = useState<string>(dateToDisplay(today));
    const [trucksToDate, setTrucksToDate] = useState<string>(dateToDisplay(fourteenDaysFromToday));
    const [trucksDatePickerFromDate, setTrucksDatePickerFromDate] = useState<any>();

    const [showStagesModal, setShowStagesModal] = useState(false);
    const [showTrucksModal, setShowTrucksModal] = useState(false);

    const [isStagesEditMode, setIsStagesEditMode] = useState(false);
    const [editedStages, setEditedStages] = useState<Record<number, number | null>>({});
    const [isSavingStages, setIsSavingStages] = useState(false);

    const [isTrucksEditMode, setIsTrucksEditMode] = useState(false);
    const [editedTrucks, setEditedTrucks] = useState<Record<number, number | null>>({});
    const [isSavingTrucks, setIsSavingTrucks] = useState(false);

    const shiftDates = (
        e: React.MouseEvent,
        previous: boolean,
        currentFrom: string,
        currentTo: string,
        setFrom: (v: string) => void,
        setTo: (v: string) => void,
    ) => {
        e.preventDefault();
        const tempFromDate = new Date(currentFrom);
        const tempToDate = new Date(currentTo);
        const step = previous ? -DAY_COUNT : DAY_COUNT;
        tempFromDate.setDate(tempFromDate.getDate() + step);
        tempToDate.setDate(tempToDate.getDate() + step);
        setFrom(dateToDisplay(tempFromDate));
        setTo(dateToDisplay(tempToDate));
    };

    const changeDate = (e: React.MouseEvent, previous: boolean) =>
        shiftDates(e, previous, fromDate, toDate, setFromDate, setToDate);

    const changeTrucksDate = (e: React.MouseEvent, previous: boolean) =>
        shiftDates(e, previous, trucksFromDate, trucksToDate, setTrucksFromDate, setTrucksToDate);

    const applyDateFilter = (
        date: any,
        setFrom: (v: string) => void,
        setTo: (v: string) => void,
        setPickerFrom: (v: any) => void,
        toggleId: string,
    ) => {
        if (moment.isMoment(date)) {
            const tempFromDate = new Date(date.valueOf());
            tempFromDate.setHours(0, 0, 0);
            if (
                tempFromDate &&
                tempFromDate.toString() !== 'Invalid Date' &&
                tempFromDate.getFullYear() > 1900
            ) {
                const tempToDate = new Date(date.valueOf());
                tempToDate.setDate(tempToDate.getDate() + DAY_COUNT - 1);
                tempToDate.setHours(0, 0, 0);
                setFrom(dateToDisplay(tempFromDate));
                setPickerFrom(dateToDisplay(tempFromDate));
                setTo(dateToDisplay(tempToDate));
                document.getElementById(toggleId)?.click();
            }
        }
    };

    const changeDateFilter = (date: any) =>
        applyDateFilter(date, setFromDate, setToDate, setDatePickerFromDate, 'JobMetrics-DateFilter-Container');

    const changeTrucksDateFilter = (date: any) =>
        applyDateFilter(
            date,
            setTrucksFromDate,
            setTrucksToDate,
            setTrucksDatePickerFromDate,
            'JobMetrics-TrucksDateFilter-Container',
        );

    const buildDateRange = (rangeFromDate: string) => {
        const list: string[] = [];
        const days: Date[] = [];
        const cursor = new Date(rangeFromDate);
        for (let i = 0; i < DAY_COUNT; i++) {
            days.push(startOfDay(cursor));
            const m = cursor.getMonth() + 1;
            const d = cursor.getDate();
            list.push(`${m < 10 ? `0${m}` : m}/${d < 10 ? `0${d}` : d}${[6, 0].includes(cursor.getDay()) ? '|' : ''}`);
            cursor.setDate(cursor.getDate() + 1);
        }
        return { dateList: list, dayDates: days };
    };

    const { dateList, dayDates } = useMemo(() => buildDateRange(fromDate), [fromDate]);
    const { dateList: trucksDateList, dayDates: trucksDayDates } = useMemo(
        () => buildDateRange(trucksFromDate),
        [trucksFromDate],
    );

    const {
        data: plannedStagesData,
        loading: plannedStagesLoading,
        refetch: refetchStages,
    } = useQuery<GetJobPlannedStagesResponse>(GET_JOB_PLANNED_STAGES, {
        variables: { jobId: jobIdVal },
        fetchPolicy: 'network-only',
        skip: !jobIdVal,
    });

    const {
        data: plannedTrucksData,
        loading: plannedTrucksLoading,
        refetch: refetchTrucks,
    } = useQuery<GetJobPlannedTrucksResponse>(GET_JOB_PLANNED_TRUCKS, {
        variables: { jobId: jobIdVal },
        fetchPolicy: 'network-only',
        skip: !jobIdVal,
    });

    const { data: jobSummaryData } = useQuery<GetJobSummaryInfo>(GET_JOB_SUMMARY_INFO, {
        variables: { jobId: jobIdVal },
        fetchPolicy: 'cache-first',
        skip: !jobIdVal,
    });

    const jobName =
        jobSummaryData?.getJobSummaryInfo?.find((s) => s?.jobName)?.jobName ??
        localStorage.getItem('Jobdetails-TabName') ??
        '';

    const [saveStagesMutation] = useMutation<{ saveJobPlannedStages: JobMetricsMutationResponse }>(
        SAVE_JOB_PLANNED_STAGES,
    );
    const [saveTrucksMutation] = useMutation<{ saveJobPlannedTrucks: JobMetricsMutationResponse }>(
        SAVE_JOB_PLANNED_TRUCKS,
    );

    const stagesEntries: JobMetricHistoryEntry[] = useMemo(
        () =>
            (plannedStagesData?.getJobPlannedStages ?? [])
                .filter((r): r is NonNullable<typeof r> => r !== null)
                .map((r) => ({
                    id: r.id,
                    fromDate: r.fromDate,
                    toDate: r.toDate,
                    value: r.stagesPerDay,
                }))
                .sort((a, b) => (b.id ?? 0) - (a.id ?? 0)),
        [plannedStagesData],
    );

    const trucksEntries: JobMetricHistoryEntry[] = useMemo(
        () =>
            (plannedTrucksData?.getJobPlannedTrucks ?? [])
                .filter((r): r is NonNullable<typeof r> => r !== null)
                .map((r) => ({
                    id: r.id,
                    fromDate: r.fromDate,
                    toDate: r.toDate,
                    value: r.trucksPerDay,
                }))
                .sort((a, b) => (b.id ?? 0) - (a.id ?? 0)),
        [plannedTrucksData],
    );

    const handleSaveStagesEntry = async (payload: JobMetricValueSubmit): Promise<void> => {
        try {
            const result = await saveStagesMutation({
                variables: {
                    input: {
                        jobId: jobIdVal,
                        fromDate: dateToUtcMidnightIso(payload.fromDate),
                        toDate: dateToUtcMidnightIso(payload.toDate),
                        stagesPerDay: payload.value,
                    },
                },
            });
            const errors = result?.data?.saveJobPlannedStages?.errors?.filter((e) => !!e) ?? [];
            if (errors.length > 0) {
                toast.error(`Failed to save planned stages: ${errors.join(', ')}`, toastOpt);
                return;
            }
            toast.success('Planned stages saved successfully.', toastOpt);
            setShowStagesModal(false);
            await refetchStages();
        } catch (e) {
            toast.error('Failed to save planned stages.', toastOpt);
        }
    };

    const handleSaveTrucksEntry = async (payload: JobMetricValueSubmit): Promise<void> => {
        try {
            const result = await saveTrucksMutation({
                variables: {
                    input: {
                        jobId: jobIdVal,
                        fromDate: dateToUtcMidnightIso(payload.fromDate),
                        toDate: dateToUtcMidnightIso(payload.toDate),
                        trucksPerDay: payload.value,
                    },
                },
            });
            const errors = result?.data?.saveJobPlannedTrucks?.errors?.filter((e) => !!e) ?? [];
            if (errors.length > 0) {
                toast.error(`Failed to save planned trucks: ${errors.join(', ')}`, toastOpt);
                return;
            }
            toast.success('Planned trucks saved successfully.', toastOpt);
            setShowTrucksModal(false);
            await refetchTrucks();
        } catch (e) {
            toast.error('Failed to save planned trucks.', toastOpt);
        }
    };

    const handleEnterStagesEditMode = () => {
        setEditedStages({});
        setIsStagesEditMode(true);
    };
    const handleCancelStagesEdit = () => {
        setEditedStages({});
        setIsStagesEditMode(false);
    };
    const handleStagesCellChange = (dayIndex: number, value: number | null) => {
        setEditedStages((prev) => ({ ...prev, [dayIndex]: value }));
    };

    const handleSaveStagesEdits = async () => {
        const keys = Object.keys(editedStages);
        if (keys.length === 0) {
            handleCancelStagesEdit();
            return;
        }
        setIsSavingStages(true);
        try {
            const errorMessages: string[] = [];
            for (const key of keys) {
                const dayIndex = parseInt(key, 10);
                if (!Number.isFinite(dayIndex)) continue;
                const day = dayDates[dayIndex];
                if (!day) continue;
                const val = editedStages[dayIndex];
                const numeric = val == null || !Number.isFinite(val) ? 0 : (val as number);
                const iso = dateToUtcMidnightIso(day);
                const result = await saveStagesMutation({
                    variables: {
                        input: {
                            jobId: jobIdVal,
                            fromDate: iso,
                            toDate: iso,
                            stagesPerDay: numeric,
                        },
                    },
                });
                const errs = result?.data?.saveJobPlannedStages?.errors?.filter((e) => !!e) ?? [];
                errs.forEach((e) => e && errorMessages.push(e));
            }
            if (errorMessages.length > 0) {
                toast.error(`Failed to update planned stages: ${errorMessages.join(', ')}`, toastOpt);
            } else {
                toast.success('Planned stages updated successfully.', toastOpt);
                setEditedStages({});
                setIsStagesEditMode(false);
                await refetchStages();
            }
        } catch (e) {
            toast.error('Failed to update planned stages.', toastOpt);
        } finally {
            setIsSavingStages(false);
        }
    };

    const handleEnterTrucksEditMode = () => {
        setEditedTrucks({});
        setIsTrucksEditMode(true);
    };
    const handleCancelTrucksEdit = () => {
        setEditedTrucks({});
        setIsTrucksEditMode(false);
    };
    const handleTrucksCellChange = (dayIndex: number, value: number | null) => {
        setEditedTrucks((prev) => ({ ...prev, [dayIndex]: value }));
    };

    const handleSaveTrucksEdits = async () => {
        const keys = Object.keys(editedTrucks);
        if (keys.length === 0) {
            handleCancelTrucksEdit();
            return;
        }
        setIsSavingTrucks(true);
        try {
            const errorMessages: string[] = [];
            for (const key of keys) {
                const dayIndex = parseInt(key, 10);
                if (!Number.isFinite(dayIndex)) continue;
                const day = trucksDayDates[dayIndex];
                if (!day) continue;
                const val = editedTrucks[dayIndex];
                const numeric = val == null || !Number.isFinite(val) ? 0 : (val as number);
                const iso = dateToUtcMidnightIso(day);
                const result = await saveTrucksMutation({
                    variables: {
                        input: {
                            jobId: jobIdVal,
                            fromDate: iso,
                            toDate: iso,
                            trucksPerDay: numeric,
                        },
                    },
                });
                const errs = result?.data?.saveJobPlannedTrucks?.errors?.filter((e) => !!e) ?? [];
                errs.forEach((e) => e && errorMessages.push(e));
            }
            if (errorMessages.length > 0) {
                toast.error(`Failed to update planned trucks: ${errorMessages.join(', ')}`, toastOpt);
            } else {
                toast.success('Planned trucks updated successfully.', toastOpt);
                setEditedTrucks({});
                setIsTrucksEditMode(false);
                await refetchTrucks();
            }
        } catch (e) {
            toast.error('Failed to update planned trucks.', toastOpt);
        } finally {
            setIsSavingTrucks(false);
        }
    };

    if (!jobIdVal) {
        return (
            <div className="JobMetrics-Container">
                <div className="JobMetrics-EmptyState">Missing job id.</div>
            </div>
        );
    }

    const renderDateControlsBar = (
        rangeFromDate: string,
        rangeToDate: string,
        pickerValue: any,
        onPickerChange: (value: any) => void,
        onPrevNext: (e: React.MouseEvent, previous: boolean) => void,
        toggleId: string,
    ): React.ReactElement => (
        <div className="DailySandAllocations-FilterPrevNext-Container">
            <Dropdown>
                <Dropdown.Toggle variant="outline-dark" id={toggleId}>
                    <span className="DailySandAllocations-DateFilter-Text">
                        {`${rangeFromDate.split(' ')[0].replaceAll('-', '/')} -
                             ${rangeToDate.split(' ')[0].replaceAll('-', '/')}`}
                    </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="DailySandAllocations-DateFilter-Dropdown-Container">
                    <div className="DailySandAllocations-DateFilter-Dropdown">
                        <DateTimePicker
                            dateOnly
                            label="Choose FROM date"
                            onChange={onPickerChange}
                            error={''}
                            value={pickerValue}
                            open={true}
                        />
                    </div>
                </Dropdown.Menu>
            </Dropdown>

            <div className="DailySandAllocations-PrevNext-Container">
                <div
                    className="DailySandAllocations-Button DailySandAllocations-Prev-Button"
                    onClick={(e) => onPrevNext(e, true)}
                >
                    <span className="DailySandAllocations-Button-Text">
                        <i className="material-icons">chevron_left</i>
                        Previous
                    </span>
                </div>
                <div className="DailySandAllocations-Button" onClick={(e) => onPrevNext(e, false)}>
                    <span className="DailySandAllocations-Button-Text">
                        Next
                        <i className="material-icons">chevron_right</i>
                    </span>
                </div>
            </div>
        </div>
    );

    const renderDateControls = (): React.ReactElement =>
        renderDateControlsBar(
            fromDate,
            toDate,
            datePickerFromDate,
            changeDateFilter,
            changeDate,
            'JobMetrics-DateFilter-Container',
        );

    const renderTrucksDateControls = (): React.ReactElement =>
        renderDateControlsBar(
            trucksFromDate,
            trucksToDate,
            trucksDatePickerFromDate,
            changeTrucksDateFilter,
            changeTrucksDate,
            'JobMetrics-TrucksDateFilter-Container',
        );

    const renderStagesActions = (): React.ReactElement | null => {
        if (!canEditJobMetrics) return null;
        return (
            <div className="DailySandAllocations-Tab-Container">
                {!isStagesEditMode && (
                    <>
                        <div className="DailySandAllocations-Button" onClick={() => setShowStagesModal(true)}>
                            <span className="DailySandAllocations-Button-Text">
                                <i className="material-icons DailySandAllocations-Set-Icon">add_circle</i>
                                Set Planned Stages
                            </span>
                        </div>
                        <div className="DailySandAllocations-Button" onClick={handleEnterStagesEditMode}>
                            <span className="DailySandAllocations-Seperator">|</span>
                            <span className="DailySandAllocations-Button-Text">
                                <i className="material-icons DailySandAllocations-Set-Icon">edit</i>
                                Edit Stages
                            </span>
                        </div>
                    </>
                )}
                {isStagesEditMode && (
                    <>
                        <div
                            className="DailySandAllocations-Button"
                            onClick={() => {
                                if (!isSavingStages) handleSaveStagesEdits();
                            }}
                        >
                            <span className="DailySandAllocations-Button-Text">
                                <i className="material-icons DailySandAllocations-Set-Icon">save</i>
                                {isSavingStages ? 'Saving...' : 'Save'}
                            </span>
                        </div>
                        <div
                            className="DailySandAllocations-Button"
                            onClick={() => {
                                if (!isSavingStages) handleCancelStagesEdit();
                            }}
                        >
                            <span className="DailySandAllocations-Seperator">|</span>
                            <span className="DailySandAllocations-Button-Text">
                                <i className="material-icons DailySandAllocations-Set-Icon">close</i>
                                Cancel
                            </span>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderTrucksActions = (): React.ReactElement | null => {
        if (!canEditJobMetrics) return null;
        return (
            <div className="DailySandAllocations-Tab-Container">
                {!isTrucksEditMode && (
                    <>
                        <div className="DailySandAllocations-Button" onClick={() => setShowTrucksModal(true)}>
                            <span className="DailySandAllocations-Button-Text">
                                <i className="material-icons DailySandAllocations-Set-Icon">add_circle</i>
                                Set Planned Trucks
                            </span>
                        </div>
                        <div className="DailySandAllocations-Button" onClick={handleEnterTrucksEditMode}>
                            <span className="DailySandAllocations-Seperator">|</span>
                            <span className="DailySandAllocations-Button-Text">
                                <i className="material-icons DailySandAllocations-Set-Icon">edit</i>
                                Edit Trucks
                            </span>
                        </div>
                    </>
                )}
                {isTrucksEditMode && (
                    <>
                        <div
                            className="DailySandAllocations-Button"
                            onClick={() => {
                                if (!isSavingTrucks) handleSaveTrucksEdits();
                            }}
                        >
                            <span className="DailySandAllocations-Button-Text">
                                <i className="material-icons DailySandAllocations-Set-Icon">save</i>
                                {isSavingTrucks ? 'Saving...' : 'Save'}
                            </span>
                        </div>
                        <div
                            className="DailySandAllocations-Button"
                            onClick={() => {
                                if (!isSavingTrucks) handleCancelTrucksEdit();
                            }}
                        >
                            <span className="DailySandAllocations-Seperator">|</span>
                            <span className="DailySandAllocations-Button-Text">
                                <i className="material-icons DailySandAllocations-Set-Icon">close</i>
                                Cancel
                            </span>
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="Messages-Container">
            <div className="DailySandAllocations-Header-Container">
                <div className="DailySandAllocations-Headers">
                    <div className="JobProfile-HeaderTitle DailySandAllocation-HeaderTitle">
                        Job Metrics{jobName ? `: ${jobName}` : ''}
                    </div>
                    <JobMenuItems jobId={jobId ?? '0'} menu="jobMetrics" />
                </div>
            </div>

            <ToastContainer />

            <div className="DailySandAllocations-Tab-Button-Container">
                <div className="JobMetrics-SectionHeader-Group">
                    <div className="DailySandAllocationHeader JobMetrics-SectionHeader-Title">
                        Planned Stages per Day
                    </div>
                    {renderStagesActions()}
                </div>
                {renderDateControls()}
            </div>
            <div className="DailySandAllocationsTables-Container">
                {plannedStagesLoading && (
                    <div className="DailySandAllocations-Spinner-Container">
                        <Loading />
                    </div>
                )}
                <JobMetricMatrixTable
                    rowLabel="Planned Stages"
                    valueLabel="Stages / Day"
                    dateList={dateList}
                    dayDates={dayDates}
                    entries={stagesEntries}
                    loading={plannedStagesLoading}
                    isEditMode={isStagesEditMode}
                    editedValues={editedStages}
                    onCellChange={handleStagesCellChange}
                />
            </div>

            <div className="DailySandAllocations-Tab-Button-Container">
                <div className="JobMetrics-SectionHeader-Group">
                    <div className="DailySandAllocationHeader JobMetrics-SectionHeader-Title">
                        Planned Trucks per Day
                    </div>
                    {renderTrucksActions()}
                </div>
                {renderTrucksDateControls()}
            </div>
            <div className="DailySandAllocationsTables-Container">
                {plannedTrucksLoading && (
                    <div className="DailySandAllocations-Spinner-Container">
                        <Loading />
                    </div>
                )}
                <JobMetricMatrixTable
                    rowLabel="Planned Trucks"
                    valueLabel="Trucks / Day"
                    dateList={trucksDateList}
                    dayDates={trucksDayDates}
                    entries={trucksEntries}
                    loading={plannedTrucksLoading}
                    isEditMode={isTrucksEditMode}
                    editedValues={editedTrucks}
                    onCellChange={handleTrucksCellChange}
                />
            </div>

            <JobMetricEntryModal
                show={showStagesModal}
                title="Set Planned Stages per Day"
                fieldLabel="Stages / Day"
                onClickHide={() => setShowStagesModal(false)}
                onClickSave={handleSaveStagesEntry}
            />
            <JobMetricEntryModal
                show={showTrucksModal}
                title="Set Planned Trucks per Day"
                fieldLabel="Trucks / Day"
                onClickHide={() => setShowTrucksModal(false)}
                onClickSave={handleSaveTrucksEntry}
            />
        </div>
    );
};

export default JobMetrics;
